/** Canonical set of worker message types and their Next.js API routes. */
export const WORKER_ROUTE_MAP = {
  PROCESS_FILE: "/api/worker/process-file",
  PROCESS_SHAREPOINT_FILE: "/api/worker/process-sharepoint-file",
  COMPUTE_HIGHLIGHTS: "/api/worker/compute-highlights",
  REINDEX_FILE: "/api/worker/reindex-file",
  AGENT_AI_INSTANCE_PROVISION: "/api/worker/agent-ai-instance-provision",
} as const;

export type WorkerMessageType = keyof typeof WORKER_ROUTE_MAP;

export type WorkerMessage = {
  type: WorkerMessageType;
  payload: Record<string, unknown>;
  metadata: { timestamp: string; correlationId: string };
};

/**
 * Upper bound for long-running reindex handlers. The reindex route mirrors
 * this value as the literal `600` because Next.js's static route-segment
 * analyser does not accept imported constants. Worker routes that can run RAG
 * embedding work mirror the same literal; lighter routes can keep shorter
 * route-specific literals.
 */
export const WORKER_ROUTE_MAX_DURATION_SECONDS = 600;

/**
 * Headroom subtracted from {@link WORKER_ROUTE_MAX_DURATION_SECONDS} when
 * deriving the in-handler `AbortSignal.timeout` deadline. Lets the handler
 * surface a clean 5xx (and lets `finally` blocks release pool reservations
 * and advisory locks) before the route segment's hard `maxDuration` cap
 * triggers an abrupt termination.
 */
export const WORKER_ROUTE_DEADLINE_BUFFER_SECONDS = 30;

/**
 * Delivery count at which the Service Bus bridge marks a worker route request
 * as the final retry attempt. Route handlers use the header this controls to
 * decide whether a failure should become user-visible or stay retry-in-flight.
 */
export const WORKER_RETRY_EXHAUSTED_DELIVERY_COUNT = 10;
